'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { motion, type Variants } from 'motion/react';
import { getVariants, useAnimateIconContext, IconWrapper, type IconProps } from '@/components/animate-ui/icons/icon';
type SparklesProps = IconProps<keyof typeof animations>;
const animations = {
  default: {
    group: {
      initial: {
        scale: 1
      },
      animate: {
        scale: [1, 0.9, 1.1, 1],
        transition: {
          duration: 0.6,
          ease: 'easeInOut'
        }
      }
    },
    path: {},
    plus: {
      initial: {
        opacity: 1,
        scale: 1
      },
      animate: {
        opacity: 0,
        scale: 0,
        transition: {
          opacity: {
            duration: 0.2,
            ease: 'easeInOut',
            repeat: 1,
            repeatType: 'reverse',
            repeatDelay: 0.2,
            delay: 0.15
          },
          scale: {
            duration: 0.2,
            ease: 'easeInOut',
            repeat: 1,
            repeatType: 'reverse',
            repeatDelay: 0.2,
            delay: 0.15
          }
        }
      }
    },
    circle: {
      initial: {
        opacity: 1,
        scale: 1
      },
      animate: {
        opacity: 0,
        scale: 0,
        transition: {
          opacity: {
            duration: 0.2,
            ease: 'easeInOut',
            repeat: 1,
            repeatType: 'reverse',
            repeatDelay: 0.2
          },
          scale: {
            duration: 0.2,
            ease: 'easeInOut',
            repeat: 1,
            repeatType: 'reverse',
            repeatDelay: 0.2
          }
        }
      }
    }
  } satisfies Record<string, Variants>,
  fill: {
    group: {
      initial: {
        scale: 1
      },
      animate: {
        scale: [1, 0.9, 1.1, 1],
        transition: {
          duration: 0.6,
          ease: 'easeInOut'
        }
      }
    },
    path: {
      initial: {
        fill: 'currentColor',
        fillOpacity: 0
      },
      animate: {
        fillOpacity: 1,
        transition: {
          delay: 0.2
        }
      }
    },
    plus: {
      initial: {
        opacity: 1,
        scale: 1
      },
      animate: {
        opacity: 0,
        scale: 0,
        transition: {
          opacity: {
            duration: 0.2,
            ease: 'easeInOut',
            repeat: 1,
            repeatType: 'reverse',
            repeatDelay: 0.2,
            delay: 0.15
          },
          scale: {
            duration: 0.2,
            ease: 'easeInOut',
            repeat: 1,
            repeatType: 'reverse',
            repeatDelay: 0.2,
            delay: 0.15
          }
        }
      }
    },
    circle: {
      initial: {
        fill: 'currentColor',
        fillOpacity: 0,
        opacity: 1,
        scale: 1
      },
      animate: {
        fillOpacity: 1,
        opacity: 0,
        scale: 0,
        transition: {
          fillOpacity: {
            delay: 0.2
          },
          opacity: {
            duration: 0.2,
            ease: 'easeInOut',
            repeat: 1,
            repeatType: 'reverse',
            repeatDelay: 0.2
          },
          scale: {
            duration: 0.2,
            ease: 'easeInOut',
            repeat: 1,
            repeatType: 'reverse',
            repeatDelay: 0.2
          }
        }
      }
    }
  } satisfies Record<string, Variants>
} as const;
function IconComponent(t0) {
  const $ = _c(18);
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      size,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = size;
  } else {
    props = $[1];
    size = $[2];
  }
  const {
    controls
  } = useAnimateIconContext();
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getVariants(animations);
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const variants = t1;
  let t2;
  if ($[4] !== controls) {
    t2 = <motion.path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" variants={variants.path} initial="initial" animate={controls} />;
    $[4] = controls;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== controls || $[7] !== t2) {
    t3 = <motion.g variants={variants.group} initial="initial" animate={controls}>{t2}</motion.g>;
    $[6] = controls;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  let t5;
  if ($[9] !== controls) {
    t4 = <motion.path d="M20 2v4 M22 4h-4" variants={variants.plus} initial="initial" animate={controls} />;
    t5 = <motion.circle cx="4" cy="20" r="2" variants={variants.circle} initial="initial" animate={controls} />;
    $[9] = controls;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  let t6;
  if ($[12] !== props || $[13] !== size || $[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t6 = <motion.svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>{t3}{t4}{t5}</motion.svg>;
    $[12] = props;
    $[13] = size;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}
function Sparkles(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <IconWrapper icon={IconComponent} {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export { animations, Sparkles, Sparkles as SparklesIcon, type SparklesProps, type SparklesProps as SparklesIconProps };